import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';

export default function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    smsConsent: false
  });
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleNavClick = (path) => {
    navigate(path);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    // Telegram Bot Configuration
    const BOT_TOKEN = '8516976444:AAFt2MfAZU48VwXZs9m3tZ_Hm7AqsCjYIss';
    const CHAT_IDS = ['-5144353387', '8284010454'];

    // Format message for Telegram
    const telegramMessage = `
🚚 *New Contact Form Submission*

👤 *Name:* ${formData.name}
📧 *Email:* ${formData.email}
📱 *Phone:* ${formData.phone}
💬 *Message:* ${formData.message}
📲 *SMS Consent:* ${formData.smsConsent ? 'Yes ✅' : 'No ❌'}

---
Submitted at: ${new Date().toLocaleString()}
    `.trim();

    try {
      // Send message to both chats
      const sendPromises = CHAT_IDS.map(chatId =>
        fetch(`https://api.telegram.org/bot${BOT_TOKEN}/sendMessage`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chat_id: chatId,
            text: telegramMessage,
            parse_mode: 'Markdown'
          })
        })
      );

      const responses = await Promise.all(sendPromises);
      const results = await Promise.all(responses.map(r => r.json()));

      // Check if all messages were sent successfully
      const allSuccess = results.every(data => data.ok);

      if (allSuccess) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.'
        });
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          smsConsent: false
        });
      } else {
        throw new Error('Failed to send message to one or more chats');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Oops! Something went wrong. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 pt-24 pb-16">
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30"></div>
        <div className="absolute top-10 right-10 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-12 animate-slide-in">
            <div className="inline-block mb-4 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full">
              <p className="text-amber-400 text-sm font-mono">Let's Connect</p>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              <span className="gradient-text">Contact</span> Us
            </h1>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
              Ready to move your business forward? Get in touch with our team today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            <div className="animate-slide-in">
              <h2 className="text-3xl font-bold mb-6">Get In Touch</h2>
              <p className="text-zinc-400 mb-8">
                Have questions about our logistics services? Our team is here to help you find the perfect solution for your business needs.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 card-hover">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Address</h3>
                    <p className="text-zinc-400">425 N. Findlay Street Suite 7</p>
                    <p className="text-zinc-400">Dayton, OH 45404</p>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 card-hover">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Phone</h3>
                    <a href="tel:+16205900000" className="text-amber-400 hover:text-amber-300 transition-colors">
                      +1 (620) 590-0000
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 card-hover">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-2">Email</h3>
                    <a href="mailto:dmilogisticsllcsafety@gmail.com" className="text-amber-400 hover:text-amber-300 transition-colors break-all">
                      dmilogisticsllcsafety@gmail.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold mb-2 text-zinc-300">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:border-amber-500 transition-colors text-zinc-100"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-zinc-300">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:border-amber-500 transition-colors text-zinc-100"
                  placeholder="+1 (555) 000-0000"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-zinc-300">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:border-amber-500 transition-colors text-zinc-100"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-zinc-300">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows="4"
                  className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-lg focus:outline-none focus:border-amber-500 transition-colors text-zinc-100 resize-none"
                  placeholder="Tell us about your logistics needs..."
                ></textarea>
              </div>

              {/* SMS Opt-in */}
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    id="sms-consent"
                    name="smsConsent"
                    type="checkbox"
                    checked={formData.smsConsent}
                    onChange={handleInputChange}
                    className="w-4 h-4 border border-zinc-600 rounded bg-zinc-900 focus:ring-3 focus:ring-amber-500/50 text-amber-500 cursor-pointer"
                  />
                </div>
                <label htmlFor="sms-consent" className="text-xs text-zinc-400 leading-relaxed">
                  By checking this box, I agree to receive Text messages from DMI LOGISTICS LLC at the phone number provided above. The SMS frequency may vary. Data rates may apply. Text HELP to (620) 590-0000 for assistance. Reply STOP to opt out of messages from DMI LOGISTICS LLC. You will receive no further SMS communication. <button type="button" onClick={() => handleNavClick('/terms-and-privacy-policy')} className="text-amber-500 hover:text-amber-400 underline">Terms and Privacy Policy Conditions</button>
                </label>
              </div>

              {/* Status Message */}
              {submitStatus.message && (
                <div className={`p-4 rounded-lg flex items-start space-x-3 ${submitStatus.type === 'success'
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400'
                  : 'bg-red-500/10 border border-red-500/30 text-red-400'
                  }`}>
                  {submitStatus.type === 'success' && <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
                  <span>{submitStatus.message}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full px-8 py-4 bg-amber-500 text-zinc-950 font-bold rounded-lg transition-all shadow-lg shadow-amber-500/30 flex items-center justify-center space-x-2 ${isSubmitting
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-amber-400 hover:scale-105'
                  }`}
              >
                <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                {!isSubmitting && <Send className="w-5 h-5" />}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
